/**
 * Email verification page.
 * 
 * Handles magic link verification from registration email.
 * Shows loading, success, or safe error state.
 * 
 * Security posture:
 * - Does not expose internal token validation reasons
 * - Uses safe generic error messages for all failure cases
 * - Does not enumerate account existence
 */
import { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { verifyMagicLink, AuthError } from '@/api/auth';
import { RoutePaths } from '@/routes';

type VerifyState = 'loading' | 'success' | 'error';

export function VerifyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<VerifyState>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setState('error');
      setErrorMessage('Verification link is invalid or missing. Please request a new one.');
      return;
    }

    // Verify the token
    verifyMagicLink(token)
      .then(() => {
        setState('success');
      })
      .catch((err) => {
        setState('error');
        if (err instanceof AuthError && err.safe) {
          // Show safe generic message - do not expose internal reasons
          setErrorMessage('This verification link is invalid or has expired. Please request a new one.');
        } else {
          // Generic message for unknown errors
          setErrorMessage('Verification failed. Please request a new verification email.');
        }
      });
  }, [searchParams]);

  // Redirect to login after success
  const handleSignIn = () => {
    navigate(RoutePaths.LOGIN);
  };

  if (state === 'loading') {
    return (
      <div className="page">
        <div className="page-header">
          <h1 className="page-title">Verifying your email…</h1>
          <p className="page-description">
            Please wait while we confirm your email address.
          </p>
        </div>
        <div className="loading-indicator">
          <div className="spinner" />
        </div>
        <style>{`
          .loading-indicator {
            display: flex;
            justify-content: center;
            padding: 40px 0;
          }
          .spinner {
            width: 40px;
            height: 40px;
            border: 3px solid var(--color-border);
            border-top-color: var(--color-accent);
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="page">
        <div className="page-header">
          <h1 className="page-title">Email verification failed</h1>
        </div>
        <div className="error-box" role="alert">
          <p className="error-text">{errorMessage}</p>
        </div>
        <div className="action-buttons">
          <Link to={RoutePaths.REGISTER} className="btn btn-secondary">
            Request new verification email
          </Link>
          <Link to={RoutePaths.LOGIN} className="btn btn-link">
            Sign in instead
          </Link>
        </div>
        <style>{`
          .error-box {
            padding: 20px;
            background-color: rgba(233, 69, 96, 0.1);
            border: 1px solid var(--color-accent);
            border-radius: var(--radius);
            margin-bottom: var(--spacing);
          }
          .error-text {
            color: var(--color-text);
            margin: 0;
          }
          .action-buttons {
            display: flex;
            flex-direction: column;
            gap: 12px;
            margin-top: var(--spacing);
          }
          .btn {
            display: block;
            width: 100%;
            padding: 14px 24px;
            border-radius: var(--radius);
            font-size: 16px;
            font-weight: 600;
            text-align: center;
            text-decoration: none;
            transition: opacity 0.2s ease;
          }
          .btn:hover {
            opacity: 0.9;
          }
          .btn-secondary {
            background-color: var(--color-accent);
            color: white;
          }
          .btn-link {
            background: none;
            color: var(--color-accent);
            border: none;
          }
        `}</style>
      </div>
    );
  }

  // Success state
  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Email verified</h1>
      </div>
      <div className="success-box">
        <p className="success-title">Welcome!</p>
        <p className="success-text">
          Your email has been verified. You can now sign in to your account.
        </p>
      </div>
      <button
        type="button"
        className="btn btn-primary"
        onClick={handleSignIn}
      >
        Sign in
      </button>
      <style>{`
        .success-box {
          padding: 20px;
          background-color: rgba(46, 204, 113, 0.1);
          border: 1px solid #2ecc71;
          border-radius: var(--radius);
          margin-bottom: var(--spacing);
          text-align: center;
        }
        .success-title {
          font-size: 1.2rem;
          font-weight: 600;
          margin: 0 0 8px 0;
          color: #2ecc71;
        }
        .success-text {
          color: var(--color-text-muted);
          margin: 0;
        }
        .btn {
          width: 100%;
          padding: 14px 24px;
          border: none;
          border-radius: var(--radius);
          font-size: 16px;
          font-weight: 600;
          transition: opacity 0.2s ease;
          cursor: pointer;
        }
        .btn:hover {
          opacity: 0.9;
        }
        .btn-primary {
          background-color: var(--color-accent);
          color: white;
        }
      `}</style>
    </div>
  );
}
